import React, { useEffect, useRef, useState } from 'react';
import apiClient from '../services/apiClient';
import defaultNoteCover from '../assets/note-covers/default-note-cover.svg';
import PresetLibraryCoverImage from './PresetLibraryCoverImage';

const groupImageRequests = new Map();

function fetchGroupImageOnce(locale, groupId) {
  const key = `${locale}:${groupId}`;
  if (!groupImageRequests.has(key)) {
    const request = apiClient.fetchPresetLibraryGroupImage(locale, groupId)
      .catch((error) => {
        groupImageRequests.delete(key);
        throw error;
      });
    groupImageRequests.set(key, request);
  }
  return groupImageRequests.get(key);
}

function LibraryGroupImage({ locale, groupId, hasImage, fallbackPresetId, fallbackHasCover, alt = '' }) {
  const imageRef = useRef(null);
  const [source, setSource] = useState(defaultNoteCover);

  useEffect(() => {
    setSource(defaultNoteCover);
    if (!hasImage) return undefined;
    let cancelled = false;
    let objectUrl = '';
    let observer = null;

    const load = () => fetchGroupImageOnce(locale, groupId)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setSource(objectUrl);
      })
      .catch((error) => {
        if (!cancelled && error?.name !== 'AbortError') setSource(defaultNoteCover);
      });

    if (typeof IntersectionObserver === 'function' && imageRef.current) {
      observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        load();
      }, { rootMargin: '240px', threshold: 0.01 });
      observer.observe(imageRef.current);
    } else {
      load();
    }

    return () => {
      cancelled = true;
      observer?.disconnect();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [groupId, hasImage, locale]);

  if (!hasImage) {
    return <PresetLibraryCoverImage
      presetId={fallbackPresetId || ''}
      hasCover={Boolean(fallbackPresetId && fallbackHasCover)}
      alt={alt}
      className="library-group-image"
    />;
  }

  return <img
    ref={imageRef}
    src={source}
    alt={alt}
    loading="lazy"
    decoding="async"
    className={`library-group-image${source === defaultNoteCover ? ' is-fallback' : ''}`}
  />;
}

export default LibraryGroupImage;
